import {
	type AppLocale,
	DEFAULT_LOCALE,
	detectLocale,
} from "@/libraries/locale-codes";
import { createStore } from "zustand";
import { persist } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { useStoreWithEqualityFn } from "zustand/traditional";

interface LocaleStoreState {
	locale: AppLocale;
	/** True once the user has explicitly picked a language in Settings, as opposed
	 * to it being set by detection or adopted from the server profile. An explicit
	 * choice outranks everything else and is never silently overwritten. */
	isExplicit: boolean;
}

interface LocaleStoreActions {
	setLocale: (locale: AppLocale, options?: { explicit?: boolean }) => void;
	/** Adopts a locale from the backend profile, but only if the user hasn't
	 * explicitly chosen one locally - an explicit local choice always wins. */
	adoptFromProfile: (locale: AppLocale) => void;
}

type LocaleStore = LocaleStoreState & LocaleStoreActions;

export const useLocaleStore = createStore<LocaleStore>()(
	persist(
		(set, get) => ({
			locale: detectLocale(),
			isExplicit: false,

			setLocale: (locale, options) => {
				set({ locale, isExplicit: options?.explicit ?? true });
			},

			adoptFromProfile: (locale) => {
				const { isExplicit } = get();
				if (isExplicit) return;
				set({ locale });
			},
		}),
		{
			name: "locale",
			partialize: (state) => ({
				locale: state.locale,
				isExplicit: state.isExplicit,
			}),
		},
	),
);

export function useLocaleShallow<T>(selector: (store: LocaleStore) => T): T {
	return useStoreWithEqualityFn(useLocaleStore, selector, shallow);
}

/** Non-hook accessor for route loaders/beforeLoad and other code outside React. */
export function getCurrentLocale(): AppLocale {
	return useLocaleStore.getState().locale ?? DEFAULT_LOCALE;
}
